import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getCalApi } from '@calcom/embed-react';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { ScrollReveal, StaggerReveal, StaggerItem } from './ScrollReveal';
import { trackEvent } from '../analytics';
import { BOOK_DEMO_EVENT_TYPES } from '../lib/bookDemo';
import './BookDemoPage.css';

const CAL_EMBED_NAMESPACE = 'pycrust-book-demo';
const CAL_EMBED_HOST_ID = 'pycrust-book-demo-overlay-host';

const BookDemoPage = () => {
  const reduceMotion = useReducedMotion();
  const [activeEventType, setActiveEventType] = useState(null);
  const titleId = useId();
  const closeButtonRef = useRef(null);
  const previousBodyStylesRef = useRef({
    overflow: '',
    position: '',
    top: '',
    width: '',
  });
  const previousHtmlOverflowRef = useRef('');
  const lockedScrollYRef = useRef(0);

  useEffect(() => {
    trackEvent('book_demo_page_view', {
      cal_link: BOOK_DEMO_EVENT_TYPES[0]?.calLink || '',
    });
  }, []);

  useEffect(() => {
    if (!activeEventType) {
      return undefined;
    }

    trackEvent('book_demo_overlay_open', {
      event_type_id: activeEventType.id,
      cal_link: activeEventType.calLink,
    });

    // Lock page scroll (html + body). Body position:fixed prevents iOS rubber-band
    // scrolling of the page behind the overlay while Cal's iframe keeps its own scroll.
    lockedScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    previousHtmlOverflowRef.current = document.documentElement.style.overflow;
    previousBodyStylesRef.current = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollYRef.current}px`;
    document.body.style.width = '100%';

    let cancelled = false;

    (async () => {
      const cal = await getCalApi({ namespace: CAL_EMBED_NAMESPACE });
      if (cancelled) return;

      cal('ui', {
        theme: 'light',
        hideEventTypeDetails: false,
        styles: {
          branding: {
            brandColor: '#1f6feb',
          },
        },
      });

      cal('inline', {
        elementOrSelector: `#${CAL_EMBED_HOST_ID}`,
        calLink: activeEventType.calLink,
        config: {
          layout: 'month_view',
          useSlotsViewOnSmallScreen: 'true',
          theme: 'light',
        },
      });
    })();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveEventType(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);

    const focusTimer = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKeyDown);
      document.documentElement.style.overflow = previousHtmlOverflowRef.current;
      document.body.style.overflow = previousBodyStylesRef.current.overflow;
      document.body.style.position = previousBodyStylesRef.current.position;
      document.body.style.top = previousBodyStylesRef.current.top;
      document.body.style.width = previousBodyStylesRef.current.width;
      window.scrollTo(0, lockedScrollYRef.current);
      const host = document.getElementById(CAL_EMBED_HOST_ID);
      if (host) {
        host.replaceChildren();
      }
    };
  }, [activeEventType]);

  const openEventType = useCallback((eventType) => {
    trackEvent('book_demo_type_select', {
      event_type_id: eventType.id,
      cal_link: eventType.calLink,
    });
    setActiveEventType(eventType);
  }, []);

  const closeOverlay = useCallback(() => {
    if (activeEventType) {
      trackEvent('book_demo_overlay_close', {
        event_type_id: activeEventType.id,
        cal_link: activeEventType.calLink,
      });
    }
    setActiveEventType(null);
  }, [activeEventType]);

  const overlay =
    activeEventType && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="book-demo-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            <div className="book-demo-overlay-chrome">
              <button
                ref={closeButtonRef}
                type="button"
                className="book-demo-overlay-back"
                onClick={closeOverlay}
              >
                <ArrowLeft className="ui-icon-2xl" aria-hidden />
                Back
              </button>
              <div className="book-demo-overlay-heading">
                <h2 id={titleId} className="book-demo-overlay-title">
                  {activeEventType.title}
                </h2>
                <span className="book-demo-overlay-duration">
                  <Clock className="ui-icon-md" aria-hidden />
                  {activeEventType.durationLabel}
                </span>
              </div>
            </div>
            <div className="book-demo-overlay-body">
              <div
                id={CAL_EMBED_HOST_ID}
                className="book-demo-overlay-cal-host"
              />
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <section className="book-demo-page">
      <div className="book-demo-page-mesh" aria-hidden />
      <div className="container">
        <ScrollReveal direction="up" className="section-header book-demo-page-header">
          <div className="section-badge">
            <span>Demo</span>
          </div>
          <h1 className="section-title">Book a demo</h1>
          <p className="section-subtitle">
            Choose a session type, then pick a time. We&apos;ll walk through workspaces,
            client review, and publishing on Pycrust.io.
          </p>
        </ScrollReveal>

        <StaggerReveal className="book-demo-type-grid">
          {BOOK_DEMO_EVENT_TYPES.map((eventType) => (
            <StaggerItem key={eventType.id}>
              <motion.button
                type="button"
                className="book-demo-type-card"
                whileHover={reduceMotion ? undefined : { y: -4 }}
                whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                transition={{ duration: 0.25 }}
                onClick={() => openEventType(eventType)}
              >
                <div className="book-demo-type-card-top">
                  <span className="book-demo-type-duration">
                    <Clock className="ui-icon-lg" aria-hidden />
                    {eventType.durationLabel}
                  </span>
                  <ArrowRight className="ui-icon-2xl book-demo-type-arrow" aria-hidden />
                </div>
                <h2 className="book-demo-type-title">{eventType.title}</h2>
                <p className="book-demo-type-description">{eventType.description}</p>
                <span className="book-demo-type-cta">Select time</span>
              </motion.button>
            </StaggerItem>
          ))}
        </StaggerReveal>
      </div>
      {overlay}
    </section>
  );
};

export default BookDemoPage;
